// html2canvas-pro (not the classic html2canvas) — the classic one throws on
// oklch/lab colors, which Tailwind v4 emits, making PDF generation fail silently.
import html2canvas from 'html2canvas-pro';
import { jsPDF } from 'jspdf';
import i18n from '@/i18n';
import type { SimulationReport } from '@/types/evaluation.ts';
import { getScoreColor, type VendorKpiCategory } from '@/types/vendor-kpi.ts';
import { formatDate } from '@/utils/formatDate.ts';
import { toScore99 } from '@/utils/scoreFormat.ts';

// Hardcoded colors — no CSS variables, always light mode
const C = {
  primary: '#7C5CFC',
  text: '#1a1a2e',
  textLight: '#6b7280',
  border: '#e5e7eb',
  green: '#22c55e',
  orange: '#f97316',
  bg: '#ffffff',
  bgAlt: '#f9fafb',
};

function scoreBar(score: number, width = 120, height = 10): string {
  const color = getScoreColor(score);
  const pct = Math.min(score, 99) / 99;
  const filled = Math.round(width * pct);
  return `
    <div style="display:flex;align-items:center;gap:8px;">
      <div style="width:${width}px;height:${height}px;background:#e5e7eb;border-radius:${height / 2}px;overflow:hidden;">
        <div style="width:${filled}px;height:100%;background:${color};border-radius:${height / 2}px;"></div>
      </div>
      <span style="font-size:12px;font-weight:600;color:${color};min-width:24px;">${score}</span>
    </div>
  `;
}

function categoryBlock(cat: VendorKpiCategory): string {
  return `
    <div style="margin-bottom:14px;">
      <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">
        <span style="font-size:10px;font-weight:700;color:${C.primary};background:${C.primary}18;padding:2px 5px;border-radius:4px;">${cat.shortName}</span>
        <span style="font-size:12px;font-weight:600;color:${C.text};">${cat.name}</span>
      </div>
      ${cat.kpis
        .map(
          (k) => `
        <div style="display:flex;align-items:center;padding:2px 0;gap:6px;">
          <span style="font-size:10px;color:${C.textLight};flex:1;min-width:0;line-height:1.3;">${k.label}</span>
          ${scoreBar(k.score, 60, 7)}
        </div>
      `
        )
        .join('')}
    </div>
  `;
}

function vendorCardHtml(
  userName: string,
  userInitials: string,
  globalScore: number,
  categories: VendorKpiCategory[]
): string {
  const scoreColor = getScoreColor(globalScore);
  return `
    <div style="background:${C.bg};border:1px solid ${C.border};border-radius:12px;padding:16px;width:180px;flex-shrink:0;">
      <div style="text-align:center;margin-bottom:10px;">
        <div style="width:40px;height:40px;border-radius:50%;background:${C.primary};color:white;display:inline-flex;align-items:center;justify-content:center;font-weight:700;font-size:14px;">${userInitials}</div>
        <div style="margin-top:6px;font-size:13px;font-weight:600;color:${C.text};">${userName}</div>
        <div style="margin-top:2px;font-size:24px;font-weight:800;color:${scoreColor};">${globalScore}</div>
        <div style="font-size:10px;color:${C.textLight};">${i18n.t('pdf.globalScore')}</div>
      </div>
      ${categories
        .map(
          (cat) => `
        <div style="display:flex;align-items:center;gap:6px;padding:3px 0;">
          <span style="font-size:10px;font-weight:600;color:${C.textLight};width:28px;flex-shrink:0;">${cat.shortName}</span>
          ${scoreBar(cat.score, 60, 6)}
        </div>
      `
        )
        .join('')}
    </div>
  `;
}

export async function generateReportPdf(params: {
  report: SimulationReport;
  userName: string;
  userInitials: string;
  maestroName: string;
  maestroColor: string;
  sessionDate?: string;
}): Promise<void> {
  const { report, userName, userInitials, maestroName, maestroColor, sessionDate } = params;
  const vp = report.vendorProfile;
  const t = i18n.t.bind(i18n);
  const dateStr = formatDate(sessionDate);
  const globalScore = vp?.globalScore ?? toScore99(report.globalScore);
  const scoreColor = getScoreColor(globalScore);

  const container = document.createElement('div');
  container.style.cssText =
    'position:fixed;left:-9999px;top:0;width:800px;background:#ffffff;font-family:system-ui,-apple-system,sans-serif;color:#1a1a2e;padding:32px;box-sizing:border-box;';

  container.innerHTML = `
    <!-- Header -->
    <div style="display:flex;justify-content:space-between;align-items:center;padding-bottom:16px;border-bottom:2px solid ${C.primary};margin-bottom:24px;">
      <div style="font-size:18px;font-weight:700;color:${C.primary};">TheRolePlayLab</div>
      <div style="font-size:12px;color:${C.textLight};">${dateStr}</div>
    </div>

    <!-- Title -->
    <div style="margin-bottom:20px;">
      <h1 style="font-size:22px;font-weight:700;color:${C.text};margin:0 0 4px 0;">${t('pdf.title')}</h1>
      <p style="font-size:13px;color:${C.textLight};margin:0;">
        ${t('pdf.sessionWith')} <span style="font-weight:600;color:${maestroColor};">${maestroName}</span> — ${report.duration}
      </p>
    </div>

    <!-- Global score -->
    <div style="text-align:center;padding:20px 0;margin-bottom:20px;">
      <div style="display:inline-block;border:3px solid ${scoreColor};border-radius:16px;padding:16px 32px;">
        <div style="font-size:48px;font-weight:800;color:${scoreColor};line-height:1;">${globalScore}</div>
        <div style="font-size:13px;color:${C.textLight};margin-top:2px;">/99</div>
      </div>
    </div>

    <!-- Summary -->
    <div style="background:${C.bgAlt};border:1px solid ${C.border};border-radius:10px;padding:16px 20px;margin-bottom:24px;">
      <h2 style="font-size:14px;font-weight:600;color:${C.text};margin:0 0 8px 0;">${t('pdf.summary')}</h2>
      <p style="font-size:12px;line-height:1.6;color:${C.textLight};margin:0;">${report.summary}</p>
    </div>

    <!-- Vendor card + KPI grid -->
    ${
      vp
        ? `
    <div style="display:flex;gap:16px;margin-bottom:24px;">
      ${vendorCardHtml(userName, userInitials, vp.globalScore, vp.categories)}
      <div style="flex:1;min-width:0;background:${C.bg};border:1px solid ${C.border};border-radius:12px;padding:16px;">
        <h2 style="font-size:13px;font-weight:600;color:${C.text};margin:0 0 12px 0;">${t('pdf.kpiByCategory')}</h2>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:0 16px;">
          ${vp.categories.map((cat) => categoryBlock(cat)).join('')}
        </div>
      </div>
    </div>
    `
        : ''
    }

    <!-- Strengths + Improvements -->
    <div style="display:flex;gap:20px;margin-bottom:20px;">
      <div style="flex:1;background:${C.bg};border:1px solid ${C.border};border-radius:10px;padding:16px 20px;">
        <h2 style="font-size:14px;font-weight:600;color:${C.text};margin:0 0 12px 0;">${t('pdf.strengths')}</h2>
        ${report.strengths
          .map(
            (s) => `
          <div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:8px;">
            <span style="color:${C.green};font-size:10px;margin-top:3px;">●</span>
            <span style="font-size:12px;color:${C.textLight};line-height:1.5;">${s}</span>
          </div>
        `
          )
          .join('')}
      </div>
      <div style="flex:1;background:${C.bg};border:1px solid ${C.border};border-radius:10px;padding:16px 20px;">
        <h2 style="font-size:14px;font-weight:600;color:${C.text};margin:0 0 12px 0;">${t('pdf.improvements')}</h2>
        ${report.improvements
          .map(
            (s) => `
          <div style="display:flex;align-items:flex-start;gap:8px;margin-bottom:8px;">
            <span style="color:${C.orange};font-size:10px;margin-top:3px;">●</span>
            <span style="font-size:12px;color:${C.textLight};line-height:1.5;">${s}</span>
          </div>
        `
          )
          .join('')}
      </div>
    </div>

    <!-- Footer -->
    <div style="text-align:center;padding-top:16px;border-top:1px solid ${C.border};">
      <span style="font-size:10px;color:${C.textLight};">${t('pdf.generatedBy')} — ${dateStr}</span>
    </div>
  `;

  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
    });

    const imgData = canvas.toDataURL('image/png');
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const pdf = new jsPDF('p', 'mm', 'a4');

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position -= pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    // Build filename from userName
    const nameParts = params.userName.trim().split(/\s+/);
    const firstName = nameParts[0] ?? 'User';
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';
    const dateForFile = sessionDate
      ? new Date(sessionDate).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10);
    const fileName = `${t('pdf.filePrefix')}_${firstName}${lastName ? '_' + lastName : ''}_${dateForFile}.pdf`;

    pdf.save(fileName);
  } finally {
    document.body.removeChild(container);
  }
}
